import { LeaderboardRow } from '../types';

/** What the leaderboard is ordered by. */
export type RankMetric = 'composite' | 'rating' | 'paceIndex' | 'cleanAvg' | 'best';

export const RANK_METRICS: { value: RankMetric; label: string; hint: string }[] = [
  {
    value: 'composite',
    label: 'Composite score',
    hint: 'A weighted blend of pace, rating, consistency and experience. Adjust the weights below.',
  },
  {
    value: 'rating',
    label: 'Rating',
    hint: 'Elo-style: every event, each driver is compared on clean pace with every other qualified driver.',
  },
  {
    value: 'paceIndex',
    label: 'Pace index',
    hint: "Clean average relative to the fastest driver in the same event. Works across tracks.",
  },
  {
    value: 'cleanAvg',
    label: 'Clean average lap',
    hint: 'Mean of laps within 5% of the driver’s median. Only comparable on one track.',
  },
  {
    value: 'best',
    label: 'Best lap',
    hint: 'Fastest single lap. Only comparable on one track.',
  },
];

/** Metrics measured in seconds, which mean nothing across different circuits. */
export const LAP_TIME_METRICS: ReadonlySet<RankMetric> = new Set<RankMetric>(['best', 'cleanAvg']);

export interface CompositeWeights {
  pace: number;
  rating: number;
  consistency: number;
  experience: number;
}

export const DEFAULT_WEIGHTS: CompositeWeights = {
  pace: 40,
  rating: 30,
  consistency: 20,
  experience: 10,
};

type Component = {
  key: keyof CompositeWeights;
  value: (r: LeaderboardRow) => number | null;
  higherIsBetter: boolean;
};

const COMPONENTS: Component[] = [
  { key: 'pace', value: (r) => r.paceIndex, higherIsBetter: false },
  { key: 'rating', value: (r) => (r.ratedEvents > 0 ? r.rating : null), higherIsBetter: true },
  { key: 'consistency', value: (r) => r.consistencyCv, higherIsBetter: false },
  { key: 'experience', value: (r) => (r.laps > 0 ? Math.log1p(r.laps) : null), higherIsBetter: true },
];

/**
 * 0–100 per driver. Each component is scaled to 0–100 across the rows shown,
 * so the score answers "how does this driver compare with this list". A driver
 * missing a component (no qualified event, say) is scored on the rest, with
 * their weights renormalised.
 */
export function compositeScores(
  rows: LeaderboardRow[],
  weights: CompositeWeights,
): Map<string, number | null> {
  const scaled = COMPONENTS.map((c) => {
    const values = rows.map(c.value).filter((v): v is number => v !== null && Number.isFinite(v));
    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 0;
    return (r: LeaderboardRow): number | null => {
      const v = c.value(r);
      if (v === null || !Number.isFinite(v)) return null;
      if (max === min) return 50;
      const t = (v - min) / (max - min);
      return 100 * (c.higherIsBetter ? t : 1 - t);
    };
  });

  const out = new Map<string, number | null>();
  for (const row of rows) {
    let sum = 0;
    let weight = 0;
    COMPONENTS.forEach((c, i) => {
      const w = Math.max(0, weights[c.key] ?? 0);
      if (w === 0) return;
      const s = scaled[i](row);
      if (s === null) return;
      sum += w * s;
      weight += w;
    });
    out.set(row.driverId, weight > 0 ? sum / weight : null);
  }
  return out;
}

export function metricValue(
  row: LeaderboardRow,
  metric: RankMetric,
  composite: Map<string, number | null>,
): number | null {
  switch (metric) {
    case 'best':
      return row.bestSeconds;
    case 'cleanAvg':
      return row.cleanAverageSeconds;
    case 'paceIndex':
      return row.paceIndex;
    case 'rating':
      return row.ratedEvents > 0 ? row.rating : null;
    case 'composite':
      return composite.get(row.driverId) ?? null;
  }
}

/** Best first; drivers with no value for the metric go to the bottom. */
export function sortByMetric(
  rows: LeaderboardRow[],
  metric: RankMetric,
  composite: Map<string, number | null>,
): LeaderboardRow[] {
  const ascending = metric === 'best' || metric === 'cleanAvg' || metric === 'paceIndex';
  return [...rows].sort((a, b) => {
    const va = metricValue(a, metric, composite);
    const vb = metricValue(b, metric, composite);
    if (va === null && vb === null) return b.laps - a.laps;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (va !== vb) return ascending ? va - vb : vb - va;
    return b.laps - a.laps;
  });
}

/** `1.0123` → `+1.23%`; the fastest shows as `0.00%`. */
export function formatPaceIndex(index: number | null): string {
  if (index === null) return '—';
  const pct = (index - 1) * 100;
  return `${pct < 0.005 ? '' : '+'}${pct.toFixed(2)}%`;
}
